// Every delete that loses measured or priced work asks the same way (#443): the
// centred "Confirm deletion" dialog from @higgins/ui, naming what goes. A
// removal that loses nothing (an empty measuring line) goes without asking.
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@higgins/ui';

interface ConfirmRemovalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What goes, e.g. "Remove Fascia from this area? Its 3 measuring lines go with it." */
  question: ReactNode;
  onConfirm: () => void;
  /** The removal is under way (it needs the server): the dialog stays open, showing "Removing…". */
  busy?: boolean;
  /** The button that opened it; focus goes back there on Cancel. */
  returnFocus: RefObject<HTMLElement | null>;
}

/** The app's one deletion question. */
export function ConfirmRemoval({ open, onOpenChange, question, onConfirm, busy, returnFocus }: ConfirmRemovalProps) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Confirm deletion"
      description={question}
      confirmLabel="Remove"
      busyLabel="Removing…"
      destructive
      busy={busy}
      onConfirm={onConfirm}
      returnFocus={returnFocus}
    />
  );
}

interface RemoveButtonProps {
  question: ReactNode;
  onRemove: () => void;
  /** False when nothing would be lost, so it goes straight away. */
  ask?: boolean;
  /** Read out for the icon, e.g. "Remove Fascia". */
  label: string;
  title?: string;
  className: string;
  children: ReactNode;
}

/** A remove button that asks first; for a removal saved on this device (no server call). */
export function RemoveButton({ question, onRemove, ask = true, label, title, className, children }: RemoveButtonProps) {
  const [asking, setAsking] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={button} type="button" onClick={() => (ask ? setAsking(true) : onRemove())} aria-label={label} title={title} className={className}>
        {children}
      </button>
      <ConfirmRemoval
        open={asking}
        onOpenChange={setAsking}
        question={question}
        onConfirm={() => {
          setAsking(false);
          onRemove();
        }}
        returnFocus={button}
      />
    </>
  );
}

interface ServerRemoveButtonProps {
  /** The button's words, e.g. "Remove area". */
  label: string;
  /** Why it can't be removed from this device now (shown on the disabled button); null when it can. */
  blocked: string | null;
  question: ReactNode;
  /** Removes it (CRM, then this device). */
  onRemove: () => Promise<void>;
  /** Why a removal was refused (null to clear), shown by the caller under its buttons. */
  onError: (message: string | null) => void;
  /** A failed removal in the user's words. */
  describe: (err: unknown) => string;
}

/**
 * A remove that needs the server (an area, a building): asks first, stays open
 * as "Removing…" while it runs, and withdraws the question for good if the
 * thing stops being removable (signal lost) before Remove is tapped.
 */
export function ServerRemoveButton({ label, blocked, question, onRemove, onError, describe }: ServerRemoveButtonProps) {
  const [step, setStep] = useState<'idle' | 'confirm' | 'sending'>('idle');
  const button = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (blocked && step === 'confirm') setStep('idle');
  }, [blocked, step]);

  async function remove() {
    setStep('sending');
    onError(null);
    try {
      await onRemove();
    } catch (err) {
      onError(describe(err));
      setStep('idle');
    }
  }

  return (
    <>
      <button
        ref={button}
        type="button"
        onClick={() => setStep('confirm')}
        disabled={!!blocked}
        title={blocked ?? undefined}
        className="flex min-h-11 items-center gap-1 text-sm text-slate-600 disabled:opacity-50"
      >
        <Trash2 className="h-4 w-4" /> {label}
      </button>
      <ConfirmRemoval
        open={step === 'sending' || (step === 'confirm' && !blocked)}
        onOpenChange={(open) => !open && setStep('idle')}
        question={question}
        busy={step === 'sending'}
        onConfirm={() => void remove()}
        returnFocus={button}
      />
    </>
  );
}
